import type { ProductDetailsPage } from "@decocms/apps-commerce/types";
import { BreadcrumbJsonLd } from "@decocms/blocks/hooks";
import { canonicalFor, sanitizeBreadcrumb } from "~/utils/constants";
import { buildProductJsonLd, withBrandSuffix } from "./plpJsonLd";
import { markPageNotFound } from "~/server/pageStatus";

export interface Props {
  type?: string;
  jsonLD: ProductDetailsPage | null;
  titleTemplate?: string;
  descriptionTemplate?: string;
}

export function plainText(html: string | undefined, max: number): string | undefined {
  if (!html) return undefined;
  const text = html
    .replace(/<[^>]+>/g, " ")
    .replace(/\.{3,}/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return undefined;
  if (text.length <= max) return text;
  const truncated = text.slice(0, max - 1);
  const lastSpace = truncated.lastIndexOf(" ");
  const safe = lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated;
  return `${safe.trimEnd()}…`;
}

export const loader = (props: Props) => {
  const product = props.jsonLD?.product;
  // Produto inexistente/removido: a PDP mostra o "não encontrado", mas a
  // resposta era 200 e indexável. Ver src/server/pageStatus.ts.
  if (!product) {
    markPageNotFound();
    return { ...props, noIndexing: true };
  }

  return {
    ...props,
    // Array real (não `[]`) — mesma armadilha do SeoPLP: `extractSeoFromProps`
    // (@decocms/blocks) só considera "jsonLDs já emitido" quando o array tem
    // `.length`; um array vazio cai no fallback `deriveCommerceSeoFromJsonLD`,
    // que serializa a `ProductDetailsPage` bruta (todas as variantes) inteira.
    // `buildProductJsonLd` espelha o schema do `ProductJsonLd` do componente,
    // então preenchendo aqui o fallback nunca dispara.
    jsonLDs: [buildProductJsonLd(product, canonicalFor(product.url))],
    title: withBrandSuffix(product.isVariantOf?.name ?? product.name),
    description: plainText(product.description, 160),
    image: product.image?.[0]?.url,
    canonical: canonicalFor(product.url),
  };
};

export const eager = true;
export const sync = true;

export default function SeoPDP({ jsonLD }: Props) {
  const page = jsonLD;
  if (!page?.product) return null;

  // O Product JSON-LD vai pro <head> via `loader`'s `jsonLDs` (framework
  // injeta a partir de `seo.jsonLDs`) — renderizar de novo aqui duplicaria
  // o mesmo bloco no <body>.
  if (!page.breadcrumbList) return null;
  return <BreadcrumbJsonLd breadcrumb={sanitizeBreadcrumb(page.breadcrumbList)} />;
}
